import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, of, from } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { ICanteen, CanteenService, IDishDetails } from './canteen.service';
import { switchMap } from 'rxjs/operators';
import { ModalController } from '@ionic/angular';
import { CanteensPage } from './canteens/canteens.page';
import { OverlayEventDetail } from '@ionic/core';
import { AppState } from 'src/app/app.module';
import { settingsFeature } from 'src/app/ngrx/settings/settings.feature';

@Injectable({
  providedIn: 'root'
})
export class DefaultCanteenGuard  {
  private store = inject<Store<AppState>>(Store);
  private router = inject(Router);
  private canteenService = inject(CanteenService);
  private modalController = inject(ModalController);

  private canteen: Observable<{ remember: boolean; rememberedId: string | null }>;

  constructor() {
    this.canteen = this.store.pipe(select(settingsFeature.selectCanteen));
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<UrlTree | boolean> {
    return this.canteen.pipe(
      switchMap((canteen: { remember: boolean; rememberedId: string | null }) => {
        if (canteen.remember && canteen.rememberedId) {
          return of(this.router.createUrlTree(['canteen', canteen.rememberedId]));
        } else {
          return from(
            this.modalController.create({
              component: CanteensPage
            })
          ).pipe(
            switchMap((modal: HTMLIonModalElement) => {
              modal.present();
              return from(modal.onDidDismiss()).pipe(
                switchMap((selectedData: OverlayEventDetail<ICanteen>) => {
                  const selectedCanteen = selectedData.data;
                  if (selectedCanteen) {
                    return of(this.router.createUrlTree(['canteen', selectedCanteen.id], { queryParams: selectedCanteen.name }));
                  }
                  return of(false);
                })
              );
            })
          );
        }
      })
    );
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<IDishDetails[]> {
    const canteenId = route.paramMap.get('canteenId');
    if (canteenId) {
      const date = new Date();
      if (date.getDay() === 0) {
        date.setTime(date.getTime() + 24 * 60 * 60 * 1000);
      }
      if (date.getDay() === 6) {
        date.setTime(date.getTime() + 2 * 24 * 60 * 60 * 1000);
      }
      return this.canteenService.dishesByDate(canteenId, date);
    }
    return of([]);
  }
}
